/// <reference path="./types/chartjs.d.ts" />

import { TooltipItem } from 'chart.js';
import { ICourse, IUnit } from './education.interface';
import { generateProjectId } from './projects';
import { createTooltips, destroyTooltips, initReadMore, refreshAOS } from './global';
import { setChartTheme } from './themes';
import { unescapeHTMLEntities, formatDate, getYear } from './utils';
import { Modal } from 'bootstrap';

/**
 * Displays an error message in the resume section.
 * 
 * @param errorMessage - The error message to display.
 */
export function displayError(errorMessage: string): void {
	console.error(errorMessage);
	const alertHtml = `<div class="alert alert-danger"><strong>Exception:</strong> ${errorMessage}</div>`;
	const alertContainer = document.querySelector<HTMLDivElement>("#resume #mCSB_3_container");
	if (alertContainer) alertContainer.innerHTML = alertHtml + alertContainer.innerHTML;
}


function setupCurricularUnitModal() {
	const modalElement = document.getElementById('unitModal');
	const modal = new Modal(modalElement!);

	document.querySelectorAll('.unit-name[data-bs-toggle="modal"]').forEach(span => {
		span.addEventListener('click', () => {
			const unitName = span.getAttribute('data-name');
			const unitURL = span.getAttribute('data-url');
			const description = decodeURIComponent(span.getAttribute('data-description') || '');
			const projects = span.getAttribute('data-projects') ? JSON.parse(decodeURIComponent(span.getAttribute('data-projects') || '')) : null;
			const bibliography = span.getAttribute('data-bibliography') ? JSON.parse(decodeURIComponent(span.getAttribute('data-bibliography') || '')) : null;

			document.getElementById('unitModalTitle')!.innerHTML = `<span>${unitName}</span>
				<a href="${unitURL}" target="_blank" class="arrow-link curricular-unit-url" aria-label="Unit URL: ${unitName}"
					style="font-size: 0.6em;margin-left: 4px;vertical-align: middle;">
					<i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
				</a>`;

			const sections = [
				{ id: 'description', content: description },
				{ id: 'projects', content: projects ? `<ul>${projects.map((proj: string) => `
					<li><a href='#${generateProjectId(proj)}' class='anchor'>${proj.replace(/"/g, '&quot;')}</a></li>
				`).join('')}</ul>` : '' },
				{ id: 'bibliography', content: bibliography ? `
					<div class="bibliography-list">
						${Object.entries(bibliography).map(([key, entries]) => `
							<div class="bibliography-category">
								<h5 class="content-title">${key}</h5>
								<ul>
									${(entries as any[]).map(entry => `
										<li>
											<strong>${entry.url ? `<a href="${entry.url}" target="_blank">${entry.title}</a>` : entry.title}</strong>
											${entry.authors ? `<br><span class="text-muted">Author(s): ${entry.authors.join(', ')}</span>` : ''}
											${entry.publisher ? `<br><span class="text-muted">Publisher: ${entry.publisher}</span>` : ''}
											${entry.year ? `<br><span class="text-muted">Year: ${entry.year}</span>` : ''}
											${entry.edition ? `<br><span class="text-muted">Edition: ${entry.edition}</span>` : ''}
											${entry.isbn ? `<br><span class="text-muted">ISBN: ${entry.isbn}</span>` : ''}
											${entry.note ? `<br><span class="text-muted">Note: ${entry.note}</span>` : ''}
										</li>
									`).join('')}
								</ul>
							</div>
						`).join('')}
					</div>
				` : '' }
			];

			sections.forEach(({ id, content }) => {
				const tab = $(`#unit-modal-tab-${id}`).parent();
				const section = $(`#unit-modal-${id}`);
				section.html('');
				if (!content) {
					tab.hide();
				} else {
					tab.show();
					if (content.length > 1200) {
						content = "<div class=\"js-read-smore\" data-read-smore-chars=\"1200\">" + content + "</div>";
					}
					section.html(content);
				}
			});

			// Switch to first tab with content
			for (const { id, content } of sections) {
				const tab = $(`#unit-modal-tab-${id}`);
				const section = $(`#unit-modal-${id}`);
				if (section.html().trim() !== '') {
					tab.tab('show');
					break;
				}
			}

			initReadMore();
			modal.show();
		});
	});

	// Add click event listener for links inside the modal to close the modal
	modalElement!.querySelector('.modal-body')!.addEventListener('click', (event) => {
		const targetElement = event.target as HTMLElement;
		if (targetElement.tagName === 'A' && targetElement.getAttribute('target') !== '_blank'
			&& targetElement.getAttribute('role') !== 'tab' && !targetElement.classList.contains('read-smore__link')) {
			modal.hide();
		}
	});
}

/**
 * Receives an array of courses and fills the education section.
 * 
 * @param courses Array of courses to fill the education section with.
 */
export function fillEducationSection(courses: ICourse[]) {
	const container = document.querySelector<HTMLDivElement>("#educationContainer");
	if (!container) return displayError("Education container not found.");

	// Create a shallow copy and reverse to prevent mutating the original array
	const reversedCourses = [...courses].reverse();

	// Store original units to allow resetting
	reversedCourses.forEach(course => {
		course.originalUnits = [...course.units];
	});

	// Generate HTML for all courses
	const containerHtml = reversedCourses.map((course, index) => generateEducationHTML(course, index)).join('');
	container.innerHTML = containerHtml;

	// Initialize tooltips and read more after the DOM update
	requestAnimationFrame(() => {
		createTooltips('.resumeTooltip');
		createTooltips('.unitTooltip');
		setupCurricularUnitModal();
		initReadMore();
		setupScrollbar();
		reversedCourses.forEach((_, index) => initializeClassificationChart(index));

		// Attach event listeners to sort controls
		reversedCourses.forEach((_, index) => {
			const sortSelect = document.getElementById(`sort-select-${index}`) as HTMLSelectElement | null;
			if (sortSelect) {
				sortSelect.addEventListener('change', (e) => {
					const target = e.target as HTMLSelectElement;
					const selectedSort = target.value;
					const selectedOption = target.selectedOptions[0];  // The first selected option (for single selection)
					const dataOrder = selectedOption.getAttribute('data-order');
					sortUnits(index, selectedSort, dataOrder);
				});
			}
		});
	});
}

/**
 * Checks if a Date object represents a valid date.
 *
 * @param date - The Date object to check.
 * @returns True if valid, false otherwise.
 */
function isValidDate(date: Date): boolean {
	return !isNaN(date.getTime());
}

/**
 * Generates the HTML for a course.
 * 
 * @param course A course object.
 * @returns The HTML string for the courses.
 */
function generateEducationHTML(course: ICourse, courseIndex: number): string {
	const startDateFull = formatDate(course.start_date);
	const endDateFull = formatDate(course.end_date);
	const startYear = getYear(course.start_date);
	const endYear = getYear(course.end_date);

	const unitsHtml = generateUnitsHTML(course);
	const chartId = `classificationChart-${courseIndex}`;

	return `
		<div class="resume-wrapper" data-aos="zoom-in">
			<ul class="resume">
				<li>
					<div class="resume-tag">
						<i class="fa-solid fa-graduation-cap" aria-hidden="true" aria-label="Graduation Cap Icon"></i>
						<div class="resume-date">
							<span data-bs-toggle="tooltip" data-bs-placement="right"
								data-bs-container="#resume" data-bs-trigger="hover" class="resumeTooltip"
								title="${startDateFull}" aria-label="Start date: ${startDateFull}">
								${startYear}
							</span>
							<div class="separator"></div>
							<span href="#" data-bs-toggle="tooltip" data-bs-placement="right"
								data-bs-container="#resume" data-bs-trigger="hover" class="resumeTooltip"
								title="${endDateFull}" aria-label="End date: ${endDateFull}">
								${endYear}
							</span>
						</div>
					</div>
					<div class="timeline-item">
						<span class="timeline-location ${course.course.length > 50 ? "long-title" : ""}">
							<i class="fa-solid fa-location-dot" aria-hidden="true" aria-label="Location Icon"></i> ${course.location.toUpperCase()}
						</span>
						<h3 class="timeline-header">
							${unescapeHTMLEntities(course.course).toUpperCase()}
							${course.course_url ? `
								<a href="${course.course_url}" target="_blank" class="arrow-link" aria-label="Course URL">
									<i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
								</a>
							` : ''}
						</h3>
						<div class="timeline-body">
							<h4>
								<i class="fa-solid fa-building-columns resumeTooltip" title="University" aria-hidden="true" aria-label="University Icon"></i>
								&nbsp;
								${unescapeHTMLEntities(course.university).toUpperCase()}
								${course.university_url ? `
									<a href="${course.university_url}" target="_blank" class="arrow-link" aria-label="University URL">
										<i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
									</a>
								` : ''}
							</h4>
							<div class="exp-description">
								<!-- Details -->
								<h4 class="course-details">
									<span style="text-transform: none;">
										<span class="resumeTooltip courseDetailTooltip"
											title="The <strong>European Qualifications Framework (EQF)</strong> is a common
												European reference framework that helps compare the qualifications across
												different European countries. It has eight levels, ranging from basic
												(Level 1) to advanced (Level 8), and it covers formal, non-formal, and
												informal learning. The EQF aims to promote transparency and mobility by
												making qualifications easier to understand and compare for education
												institutions, employers, and individuals, enhancing lifelong learning and
												international recognition of skills across Europe."
											data-bs-toggle="tooltip" data-bs-html="true"
											data-bs-custom-class="course-details-info-tooltip">
											EQF
										</span>: ${course.eqf}
										&nbsp;&nbsp;&middot;&nbsp;&nbsp;
										<span class="resumeTooltip courseDetailTooltip"
											title="The <strong>European Credit Transfer and Accumulation System (ECTS)</strong>
												is a standard used across Europe to facilitate the recognition of academic
												qualifications and student mobility between institutions. It assigns credits
												to learning outcomes based on the workload required to achieve them, with 60
												ECTS credits representing the workload of a full academic year. One ECTS
												credit is typically equivalent to 25-30 hours of study. ECTS helps students
												transfer their credits between universities and supports lifelong learning
												by making academic achievements more transparent and comparable."
											data-bs-toggle="tooltip" data-bs-html="true"
											data-bs-custom-class="course-details-info-tooltip">
											ECTS Credits
										</span>: ${course.overall_classification.ects_credits}
										&nbsp;&nbsp;&middot;&nbsp;&nbsp;
										National Grade: ${course.overall_classification.national_grade}
										&nbsp;&nbsp;&middot;&nbsp;&nbsp;
										<!-- https://aai.tecnico.ulisboa.pt/en/nucleo-de-mobilidade-e-parcerias-internacionais/do-mundo-para-o-tecnico/informacao-util-2/sistema-de-equivalencia-ects/ -->
										<span class="resumeTooltip courseDetailTooltip"
											title="
												<strong>T&eacute;cnico's Classification Scale and Grading System</strong>
												<br><br>
												The IST classification scale ranges from 10 to 20, linearly, in which
												10 is the minimum mark to obtain approval.
												<br><br>
												The awarding of ECTS classification is based on the distribution of
												marks obtained by all the students approved in the course unit in
												the last three academic years (can be extended to the last four or
												five years, if necessary), according to the following overall
												distribution:
												<br><br>
												<ul>
												<li><strong>A</strong>: 10% highest classifications on the 10-20 mark scale.</li>
												<li><strong>B</strong>: 25% following classifications on the 10-20 mark scale.</li>
												<li><strong>C</strong>: 30% following classifications on the 10-20 mark scale.</li>
												<li><strong>D</strong>: 25% following classifications on the 10-20 mark scale.</li>
												<li><strong>E</strong>: 10% lower classifications on the 10-20 mark scale.</li>
												</ul>
											"
											data-bs-toggle="tooltip" data-bs-html="true"
											data-bs-custom-class="course-details-info-tooltip">
											ECTS Grade
										</span>
										: ${course.overall_classification.ects_grade}
									</span>
								</h4>

								<!-- Description -->
								${course.description ? `<div class="course-description">${course.description}</div>` : ''}

								<!-- Units Section -->
								<strong class="resume-title">Curricular Units</strong>
								<!-- Sort units controls -->
								<div class="sort-controls">
									<label for="sort-select-${courseIndex}">Sort Units:</label>
									<select id="sort-select-${courseIndex}" class="sort-select" data-course-index="${courseIndex}">
										<option value="">Default</option>
										<option value="national-grade" data-order="asc">National Grade (asc.)</option>
										<option value="national-grade" data-order="desc">National Grade (desc.)</option>
										<option value="ects-grade" data-order="asc">ECTS Grade (asc.)</option>
										<option value="ects-grade" data-order="desc">ECTS Grade (desc.)</option>
										<option value="year" data-order="asc">Year (asc.)</option>
										<option value="year" data-order="desc">Year (desc.)</option>
										<option value="specialization">Specialization</option>
									</select>
								</div>
								<div class="curricular-units-container">
									${unitsHtml}
								</div>

								<!-- Chart Section -->
								<strong class="resume-title">Classification System
									<span class="resumeTooltip classification-system-info"
										title="${course.classification_system.info}"
										data-bs-toggle="tooltip" data-bs-html="true" data-bs-custom-class="classification-system-info-tooltip">
											<i class="fa-solid fa-circle-info"></i>
									</span>
								</strong>
								<div class="classification-chart-container">
									<canvas id="${chartId}"></canvas>
								</div>
								${course.classification_system.note? `<p class="classification-note">${course.classification_system.note}</p>` : ''}
							</div>
						</div>
					</div>
				</li>
			</ul>
		</div>`;
}

/**
 * Check if the total ECTS credits of a course equals the sum of its currcicular units' ECTS credits.
 *
 * For B.Sc. it does not because the course was restructured.
 *
 * @param course The course to verify the ECTS sum.
 */
function checkSumECTSEqualsTotal(course: ICourse): void {
	// Check if sum of units' ECTS credits is equal to the course's ECTS credits
	const totalUnitECTSCredits = course.units.reduce((sum, unit) => {
		if (Array.isArray(unit.ects_credits)) {
			return sum + unit.ects_credits.reduce((subSum, credit) => subSum + credit, 0);
		} else {
			return sum + unit.ects_credits;
		}
	}, 0);

	if (totalUnitECTSCredits !== course.overall_classification.ects_credits) {
		console.warn(`Mismatch: Units total ${totalUnitECTSCredits} ECTS, but course total is ${course.overall_classification.ects_credits} ECTS.`);
	}
}

/**
 * Calculate average national grades of a course.
 *
 * @param course The course to calculate the curricular units' average national grades.
 */
function checkAverageNationalGrade(course: ICourse) {
	// Initialize variables to store the total weighted grade and total ECTS credits
	let totalWeightedGrade = 0;
	let totalCredits = 0;

	course.units.forEach(unit => {
		// Handle units with multiple grades and credits (e.g., dissertation)
		if (Array.isArray(unit.national_grade) && Array.isArray(unit.ects_credits)) {
			// Ensure both arrays are of the same length
			if (unit.national_grade.length === unit.ects_credits.length) {
				for (let i = 0; i < unit.national_grade.length; i++) {
					const grade = unit.national_grade[i];
					const credits = unit.ects_credits[i];

					// Only consider valid grades and credits
					if (typeof grade === 'number' && typeof credits === 'number' && credits > 0) {
						totalWeightedGrade += grade * credits;
						totalCredits += credits;
					}
				}
			} else {
				console.warn(`Mismatch in the number of grades and ECTS credits for unit: ${unit.name}`);
			}
		} 
		// Handle standard units (not dissertations) with single grade and credits
		else if (typeof unit.national_grade === 'number' && typeof unit.ects_credits === 'number' && unit.ects_credits > 0) {
			totalWeightedGrade += unit.national_grade * unit.ects_credits;
			totalCredits += unit.ects_credits;
		}
	});

	// Calculate the weighted average grade
	const averageGrade = totalCredits > 0 ? totalWeightedGrade / totalCredits : 0;
	console.info(`Course "${unescapeHTMLEntities(course.course)}" has an average national grade of ${averageGrade.toFixed(2)}.`);

	// Compare the calculated average grade with the course's final grade
	const roundedAverageGrade = Math.round(averageGrade);
	const finalGrade = course.overall_classification.national_grade;
	const gradesMatch = roundedAverageGrade === finalGrade;

	// Log a warning if the average grade doesn't match the final grade
	if (!gradesMatch) {
		console.warn(`Average grade (${roundedAverageGrade}) does not match course's final grade (${finalGrade}).`);
	}
}

/**
 * Generates the HTML string for the units of a course.
 *
 * @param units - An array of units associated with the course.
 * @returns The HTML string representing the units.
 */
function generateUnitsHTML(course: ICourse): string {
	if (!course.units || course.units.length === 0) return '';

	/* // For debugging:
	checkSumECTSEqualsTotal(course);
	checkAverageNationalGrade(course);
	*/

	const unitsList = course.units.map(unit => generateUnitHTML(unit)).join('');

	return `<div class="curricular-units">
			${unitsList}
		</div>`;
}

/**
 * Generates the HTML string for a single unit.
 *
 * @param unit - The unit data to generate the HTML for.
 * @returns The HTML string representing the unit.
 */
function generateUnitHTML(unit: IUnit): string {

	const isDissertation = (unit.specializations === "Dissertation");

	// Handle fields that can be arrays or single values
	let name = Array.isArray(unit.name) ? unit.name[0] : unit.name;
	const url = Array.isArray(unit.url) ? unit.url[0] : unit.url;
	const nationalGrade = Array.isArray(unit.national_grade) ? unit.national_grade[0] : unit.national_grade;
	const ectsGrade = Array.isArray(unit.ects_grade) ? unit.ects_grade[0] : unit.ects_grade;
	const ectsCredits = Array.isArray(unit.ects_credits) ? unit.ects_credits[0] : unit.ects_credits;

	name = unescapeHTMLEntities(name);

	const specializationsHTML = `<ul>
		${Array.isArray(unit.specializations) 
			? unit.specializations.map(spec => `<li>${spec}</li>`).join('') 
			: `<li>${unit.specializations}</li>`}
		</ul>`;

	const unitInfoHTML = `<strong>National Grade:</strong> ${nationalGrade}
		<br>
		<strong>ECTS Grade:</strong> ${ectsGrade}
		<br>
		<strong>ECTS Credits:</strong> ${ectsCredits}
		<br>
		<strong>Year:</strong> ${unit.year}
		<br>
		<strong>Specialization(s):</strong> ${specializationsHTML}`;

	if (isDissertation) {
		return `
			<span class="curricular-unit-item dissertation">
				${generateDissertationHTML(unit)}
			</span>`;
	}

	return `
		<span class="curricular-unit-item">
			<span
				class="unit-name"
				data-bs-toggle="modal" data-bs-target="#unitModal"
				data-name="${name}"
				data-url="${url}"
				data-description="${unit.description ? encodeURIComponent(unit.description) : ''}"
				data-projects="${unit.projects ? encodeURIComponent(JSON.stringify(unit.projects)) : ''}"
				data-bibliography="${unit.bibliography ? encodeURIComponent(JSON.stringify(unit.bibliography)) : ''}">
				<span class="unitTooltip" data-bs-toggle="tooltip" title="${name}">
					${name}
				</span>
			</span>
			<span class="curricular-unit-buttons">
				<span class="unitTooltip curricular-unit-info"
					title="${unitInfoHTML}"
					data-bs-toggle="tooltip" data-bs-html="true" data-bs-custom-class="curricular-unit-info-tooltip">
						<i class="fa-solid fa-circle-info"></i>
				</span>
				<a href="${url}" target="_blank" class="arrow-link curricular-unit-url" aria-label="Unit URL: ${name}">
					<i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
				</a>
			</span>
		</span>`;
}


/**
 * Generates the HTML for a dissertation unit.
 *
 * @param unit - The dissertation unit data.
 * @returns The HTML string representing the dissertation unit.
 */
function generateDissertationHTML(unit: IUnit): string {

	const specializationsHTML = `<ul>
		${Array.isArray(unit.specializations) 
			? unit.specializations.map(spec => `<li>${spec}</li>`).join('') 
			: `<li>${unit.specializations}</li>`}
		</ul>`;

	return `
		<div>
			<span
				class="unit-name unitTooltip"
				data-bs-toggle="modal" data-bs-target="#unitModal"
				data-name="${unit.name[0]}"
				data-url="${unit.url[0]}"
				data-description="${unit.description ? encodeURIComponent(unit.description) : ''}"
				data-projects="${unit.projects ? encodeURIComponent(JSON.stringify(unit.projects)) : ''}"
				data-bibliography="${unit.bibliography ? encodeURIComponent(JSON.stringify(unit.bibliography)) : ''}">
				<span class="unitTooltip" data-bs-toggle="tooltip" title="${unit.name[0]}">
					${unit.name[0]}
				</span>
			</span>
			<span class="curricular-unit-buttons">
				<a href="${unit.url[0]}" target="_blank" class="arrow-link curricular-unit-url"
					aria-label="Unit URL: ${unit.name[0]}">
					<i class="fa-solid fa-arrow-up-right-from-square"></i>
				</a>
			</span>
		</div>

		<hr>

		<div>
			<span class="unit-name unitTooltip" data-bs-toggle="tooltip" title="${unit.name[1]}">
				${unit.name[1]}
			</span>
			<span class="curricular-unit-buttons">
				<span class="unitTooltip curricular-unit-info"
					data-bs-toggle="tooltip"
					title="<strong>National Grade:</strong> ${unit.national_grade[0]}
						<br>
						<strong>ECTS Grade:</strong> ${unit.ects_grade[0]}
						<br>
						<strong>ECTS Credits:</strong> ${unit.ects_credits[0]}
						<br>
						<strong>Year:</strong> ${unit.year}
						<br>
						<strong>Specialization(s):</strong> ${specializationsHTML}"
					data-bs-html="true" data-bs-custom-class="curricular-unit-info-tooltip">
					<i class="fa-solid fa-circle-info"></i>
				</span>
				<a href="${unit.url[1]}" target="_blank" class="arrow-link curricular-unit-url"
					aria-label="Unit URL: ${unit.name[1]}">
					<i class="fa-solid fa-arrow-up-right-from-square"></i>
				</a>
			</span>
		</div>

		<div>
			<span class="unit-name unitTooltip" data-bs-toggle="tooltip" title="${unit.name[2]}">
				${unit.name[2]}
			</span>
			<span class="curricular-unit-buttons">
				<span class="unitTooltip curricular-unit-info"
					data-bs-toggle="tooltip"
					title="<strong>National Grade:</strong> ${unit.national_grade[1]}
						<br>
						<strong>ECTS Grade:</strong> ${unit.ects_grade[1]}
						<br>
						<strong>ECTS Credits:</strong> ${unit.ects_credits[1]}
						<br>
						<strong>Year:</strong> ${unit.year}
						<br>
						<strong>Specialization(s):</strong> ${specializationsHTML}"
					data-bs-html="true" data-bs-custom-class="curricular-unit-info-tooltip">
					<i class="fa-solid fa-circle-info"></i>
				</span>
				<a href="${unit.url[2]}" target="_blank" class="arrow-link curricular-unit-url"
					aria-label="Unit URL: ${unit.name[2]}">
					<i class="fa-solid fa-arrow-up-right-from-square"></i>
				</a>
			</span>
		</div>
	`;
}


/**
 * Sorts the units of a specific course based on the selected criteria.
 *
 * @param courseIndex - The index of the course whose units need to be sorted.
 * @param sortCriteria - The criteria to sort by: 'grade', 'year', or 'specialization'.
 */
function sortUnits(courseIndex: number, sortCriteria: string, dataOrder: string): void {
	const courses = globalThis.education as ICourse[];
	const course = courses[courses.length - 1 - courseIndex]; // Because courses were reversed

	if (!course || !course.units) {
		console.error(`No units data found for course index ${courseIndex}.`);
		return;
	}

	let sortedUnits = [...course.units];

	switch (sortCriteria) {
		case 'national-grade':
			sortedUnits.sort((a, b) => {
				const nationalGradeA = Array.isArray(a.national_grade) ? a.national_grade[0] : a.national_grade;
				const nationalGradeB = Array.isArray(b.national_grade) ? b.national_grade[0] : b.national_grade;
				const asc = dataOrder === 'asc';
				if (asc) {
					if (nationalGradeA < nationalGradeB) return -1;
					if (nationalGradeA > nationalGradeB) return 1;
				} else {
					if (nationalGradeA < nationalGradeB) return 1;
					if (nationalGradeA > nationalGradeB) return -1;
				}
				return 0;
			});
			break;
		case 'ects-grade':
			sortedUnits.sort((a, b) => {
				const ectsGradeA = Array.isArray(a.ects_grade) ? a.ects_grade[0] : a.ects_grade;
				const ectsGradeB = Array.isArray(b.ects_grade) ? b.ects_grade[0] : b.ects_grade;
				const gradeA = ectsGradeA.toUpperCase();
				const gradeB = ectsGradeB.toUpperCase();
				const asc = dataOrder === 'asc';
				if (asc) {
					if (gradeA < gradeB) return 1;
					if (gradeA > gradeB) return -1;
				} else {
					if (gradeA < gradeB) return -1;
					if (gradeA > gradeB) return 1;
				}
				return 0;
			});
			break;
		case 'year':
			sortedUnits.sort((a, b) => {
				const yearA = parseInt(a.year.split('/')[0]); // Assuming year is like "2011/2012"
				const yearB = parseInt(b.year.split('/')[0]);
				const asc = dataOrder === 'asc';
				if (asc) {
					return yearA - yearB;
				} else {
					return yearB - yearA;
				}
			});
			break;
		case 'specialization':
			sortedUnits.sort((a, b) => {
				const specA = Array.isArray(a.specializations) ? a.specializations[0] : a.specializations;
				const specB = Array.isArray(b.specializations) ? b.specializations[0] : b.specializations;
				return specA.localeCompare(specB);
			});
			break;
		default:
			// Reset to original order
			if (course.originalUnits) {
				sortedUnits = [...course.originalUnits];
			}
			break;
	}

	// Update the course's units temporarily for sorting
	course.units = sortedUnits;

	// Regenerate the units HTML
	const unitsHtml = generateUnitsHTML(course);
	const container = document.querySelector<HTMLDivElement>(`#educationContainer`);
	if (!container) {
		displayError("Education container not found.");
		return;
	}

	// Find the specific course's curricular units container and update its HTML
	const courseElements = container.querySelectorAll('.resume-wrapper .resume li');
	if (courseElements.length > courseIndex) {
		const courseElement = courseElements[courseIndex];
		const unitsContainer = courseElement.querySelector('.curricular-units-container');
		if (unitsContainer) {
			destroyScrollbar(); // Destroy the existing scrollbar before changing the content
			destroyTooltips('.unitTooltip') // Destroy the tooltips before changing the content
			unitsContainer.innerHTML = unitsHtml;
			// Re-initialize tooltips and read more for the updated units
			createTooltips('.unitTooltip');
			initReadMore();
			// Optionally, reinitialize scrollbar if needed
			setupScrollbar();
		}
	}
}



/**
 * Initializes the Chart.js bar chart for a course's classification system.
 * 
 * @param courseIndex The index of the course to initialize the chart for.
 */
function initializeClassificationChart(courseIndex: number): void {
	const chartId = `classificationChart-${courseIndex}`;
	const canvas = document.getElementById(chartId) as HTMLCanvasElement | null;
	if (!canvas) return console.error(`Canvas with ID ${chartId} not found.`);

	const courses = globalThis.education as ICourse[]; // Ensure `globalThis.education` is set correctly
	const course = courses[courses.length - 1 - courseIndex]; // Because courses were reversed

	if (!course || !course.classification_system) {
		return console.error(`No classification_system data for course index ${courseIndex}.`);
	}

	const graduateResults = course.classification_system?.overall_classification_results ?? [];
	const labels = graduateResults.map(entry => entry.national_grade);
	const graduatePercentages = graduateResults.map(entry => entry.overall_graduate_results_percent);
	const ectsGrades = graduateResults.map(entry => entry.ects_grade);
	const courseNationalGrade = course.overall_classification.national_grade;
	const classificationIndex = graduateResults.findIndex(classification =>
		classification.national_grade === courseNationalGrade
	);

	const rootStyle = getComputedStyle(document.documentElement);
	const colors = {
		defaultBg: rootStyle.getPropertyValue('--chart-bg-color').trim(),
		defaultBorder: rootStyle.getPropertyValue('--chart-border-color').trim(),
		highlightBg: 'rgba(99, 255, 132, 0.2)',
		highlightBorder: 'rgba(99, 255, 132, 1)',
		text: rootStyle.getPropertyValue('--chart-text-color').trim(),
		grid: rootStyle.getPropertyValue('--chart-grid-color').trim(),
		font: rootStyle.getPropertyValue('--chart-font-family').trim(),
		fontSize: parseInt(rootStyle.getPropertyValue('--chart-font-size').trim()),
	};

	// Generate the color arrays
	const { backgroundColor, borderColor } = generateColorArrays(graduateResults.length, classificationIndex, colors);

	globalThis[chartId] = new Chart(canvas, {
		type: 'bar',
		data: {
			labels: labels,
			datasets: [{
				label: 'Graduate Results (%)',
				data: graduatePercentages,
				backgroundColor: backgroundColor,
				borderColor: borderColor,
				borderWidth: 1
			}]
		},
		options: {
			responsive: true,
			maintainAspectRatio: false, // Allows the chart to fit its container
			scales: {
				y: {
					beginAtZero: true,
					grid: {
						color: colors.grid
					},
					title: {
						color: colors.text,
						display: true,
						text: 'Percentage (%)'
					},
					ticks: {
						color: colors.text,
						font: { family: colors.font, size: colors.fontSize },
						callback: function(value: any) {
							return value + '%';
						}
					}
				},
				x: {
					grid: {
						color: colors.grid
					},
					title: {
						color: colors.text,
						display: true,
						text: 'National Grades'
					},
					ticks: {
						color: colors.text,
						font: { family: colors.font, size: colors.fontSize }
					}
				}
			},
			plugins: {
				legend: {
					display: false
				},
				title: {
					display: false,
					text: 'Graduate Results by National Grade',
					color: colors.text,
					font: {
						family: colors.font,
						size: 16
					}
				},
				tooltip: {
					callbacks: {
						title: function(tooltipItems: TooltipItem<"bar">[]) {
							return '';
						},
						label: function(tooltipItem: TooltipItem<"bar">) {
							return tooltipItem.label;
							//return `${context.parsed.y}%`;
						},
						footer: function(tooltipItems: TooltipItem<"bar">[]) {
							const ectsGrade = ectsGrades[tooltipItems[0].dataIndex]; // Access the corresponding ECTS grade
							//const nationalGrade = tooltipItems[0].label; // This is the national grade (label of the bar)
							const graduatePercentage = tooltipItems[0].parsed.y; // This is the percentage value
				
							return `ECTS: ${ectsGrade}\n${graduatePercentage}%`;
						}
					}
				}
			}
		}
	});

	setChartTheme(globalThis[chartId]);
}

/**
 * Generates arrays for background and border colors.
 * 
 * @param length - The number of items in the dataset.
 * @param highlightIndex - The index of the highlighted item.
 * @param colors - The color configuration.
 * @returns The backgroundColor and borderColor arrays.
 */
function generateColorArrays(length: number, highlightIndex: number, colors: any): { backgroundColor: string[], borderColor: string[] } {
	return {
		backgroundColor: Array.from({ length }, (_, index) => index === highlightIndex ? colors.highlightBg : colors.defaultBg),
		borderColor: Array.from({ length }, (_, index) => index === highlightIndex ? colors.highlightBorder : colors.defaultBorder)
	};
}

function destroyScrollbar() {
	if ($(".curricular-units-container").hasClass("mCustomScrollbar")) {
		$(".curricular-units-container").mCustomScrollbar("destroy");
	}
}

function setupScrollbar() {
	$(".curricular-units-container").mCustomScrollbar({
		theme: "minimal-dark",
		axis: "y",
		autoDraggerLength: true
	});
}

/**
 * Retrieve all the courses data from the JSON file.
 * 
 * @returns {Promise<ICourse[]>} An array with courses.
 */
async function loadEducationJson(): Promise<ICourse[]> {
	const url = 'json/education.json?v=3';
	let response = await fetch(url);

	if (response.ok) {
		return await response.json();
	} else {
		throw new Error("Error fetching education data. Response status: " + response.status);
	}
}

export function educationSetup() {
	/// Load education
	loadEducationJson()
	.then((education) => {
		globalThis.education = education; // Assign to globalThis for access in initializeClassificationChart
		fillEducationSection(education);
		refreshAOS(); // Refresh AOS to recognize new elements
	})
	.catch((e) => {
		displayError(`Failed to load education data: ${e.message}`);
	});
}
